import React, { useState, useEffect } from 'react';
import {
  IonTextarea,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonThumbnail,
  useIonActionSheet,
} from '@ionic/react';
import {
  Box,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import * as Sentry from '@sentry/react';

import { close, videocam } from 'ionicons/icons';
import { usePhotoGallery } from '../../hooks/usePhotoGallery';
import { displayErrorText, displayAttachmentLabel } from '../../helpers/media';
import { textAreaStyles } from '../commentsStyles';
import { displayName } from '../../helpers/textManipulation';
import { nonTerminalErrorMessages } from '../../hooks/helpers/usePhotoGallery_helpers';
import { getMediaActionButtons } from '../../helpers/comments';
import { useAppState } from '../../providers/app-state/app-state-provider';
import PdfIndicator from '../../common/atoms/pdfIndicator/PdfIndicator';
import appStrings from '../../common/app_strings';

const CATCHUP_STRINGS = appStrings.catchup;

const ResponseBox = ({
  onSubmit, post, media, setMedia, onClose, initialResponse = null,
}) => {
  const [response, setResponse] = useState(initialResponse?.body || '');
  const [actionSheetResult, setActionSheetResult] = useState(null);
  const [present] = useIonActionSheet();
  const { platform } = useAppState();
  const isVideo = media?.type === 'video';
  const isPhoto = media?.type === 'photo';
  const isPdf = media?.type === 'pdf';
  const [isHandFilled, setIsHandFilled] = useState(false);

  const {
    takePhoto, takeVideo, takeWebVideo, loading, error, controller, takePdf,
  } = usePhotoGallery();

  const handleHandClick = () => {
    if (!isHandFilled) {
      setResponse(CATCHUP_STRINGS.sendMessage);
    } else {
      setResponse('');
    }
    setIsHandFilled((prev) => !prev);
  };

  const handlePhoto = async () => {
    if (loading) {
      return;
    }

    try {
      const { media: photo, webviewPath } = await takePhoto();
      if (photo) {
        setMedia({ webviewPath, id: photo.id, type: 'photo' });
      }
    } catch (e) {
      if (e.message !== nonTerminalErrorMessages.cancelledPhotosApp) {
        console.error(e);
      }
    }
  };
  const handleWebVideo = async () => {
    const videoObj = await takeWebVideo();
    if (videoObj?.media) {
      const { media: video } = videoObj;
      setMedia({ webviewPath: video.uri, id: video.id, type: 'video' });
    }
  };

  const handleMobileVideo = async () => {
    const videoObj = await takeVideo();
    if (videoObj?.media) {
      const { media: video, webviewPath } = videoObj;
      setMedia({ webviewPath, id: video.id, type: 'video' });
    }
  };

  const handleVideo = async () => {
    if (loading) {
      return;
    }
    const uploader = platform?.isNativePlatform ? handleMobileVideo : handleWebVideo;

    try {
      await uploader();
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handlePdf = async () => {
    if (loading) {
      return;
    }
    try {
      const pdfObj = await takePdf();
      if (pdfObj?.media) {
        const { webviewPath, media: pdf } = pdfObj;
        setMedia({ webviewPath, id: pdf.id, type: 'pdf' });
        setActionSheetResult(null);
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleAction = (action) => {
    switch (action) {
      case 'photo':
        handlePhoto();
        break;
      case 'video':
        handleVideo();
        break;
      default:
        // resetReplyTo();
    }
  };

  const presentActionSheet = (buttons) => {
    present({
      buttons,
      onDidDismiss: ({ detail }) => setActionSheetResult(detail),
    });
  };

  const handlePresentActionSheet = () => {
    const actionSheetButtons = getMediaActionButtons();
    presentActionSheet(actionSheetButtons);
  };

  const handleSubmit = async () => {
    if (!response.trim() && !media) return;

    const payload = {
      response: response.trim(),
      media,
      postId: post.id,
      commentId: initialResponse?.id || null,
    };

    await onSubmit(payload);

    setResponse('');
    setMedia(null);
    onClose();
  };

  const handleMediaClose = () => {
    if (loading) controller.abort();
    setMedia(null);
  };

  useEffect(() => {
    if (actionSheetResult?.data) {
      const {
        data: { action },
      } = actionSheetResult;
      handleAction(action);
    }
  }, [actionSheetResult]);

  useEffect(() => {
    if (initialResponse?.body) {
      setResponse(initialResponse?.body);
    }
    if (initialResponse?.media) {
      setMedia(initialResponse?.media);
    }
  }, [initialResponse]);

  useEffect(() => {
    const container = document.querySelector('.catchup-card-container');
    const content = document.querySelector('.catchup-card-content');

    const resizeObserver = new ResizeObserver(() => {
      content?.scrollTo({
        top: content.scrollHeight,
        behavior: 'smooth',
      });
    });

    const handleKeyboardShow = () => {
      container?.classList.add('keyboard-open');
      if (!container) return;
      resizeObserver.observe(container);
    };

    const handleKeyboardHide = () => {
      container?.classList.remove('keyboard-open');
      resizeObserver.disconnect();
    };

    window.addEventListener('ionKeyboardDidShow', handleKeyboardShow);
    window.addEventListener('ionKeyboardDidHide', handleKeyboardHide);

    return () => {
      window.removeEventListener('ionKeyboardDidShow', handleKeyboardShow);
      window.removeEventListener('ionKeyboardDidHide', handleKeyboardHide);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box className="response-box">
      <Box className="response-textarea-container">
        <IonTextarea
          value={response}
          placeholder={CATCHUP_STRINGS.respondTo(displayName(post?.author?.first_name, post?.author?.last_name))}
          onIonChange={(e) => {
            setResponse(e.detail.value);
            const textarea = e.target;
            const isScrollable = textarea.scrollHeight > textarea.clientHeight;
            textarea.style.setProperty('--is-scrollable', isScrollable ? '1' : '0');
          }}
          autoGrow
          className="comment-input"
          rows={1}
          spellcheck
          style={{
            ...textAreaStyles,
            '--has-media': (media?.webviewPath || loading) ? '1' : '0',
            '--is-scrollable': '0',
            marginLeft: 0,
          }}
          autocapitalize="on"
          enterkeyhint="enter"
          autofocus
          onClick={(e) => (e?.target?.setFocus ? e?.target?.setFocus() : null)}
        />
      </Box>

      {/* Media Preview */}
      <Box>
        {error && (
          <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
            {displayErrorText(error)}
          </Alert>
        )}
      </Box>
      {(media?.webviewPath || loading) && !error && (
        <IonItem
          detail={false}
          lines="none"
          className="post-option-item post-media-preview"
          style={{ marginLeft: '0.625rem', lineHeight: 'normal' }}
        >
          {loading && (
            <Box sx={{ paddingRight: '.75rem' }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loading && media?.webviewPath && isPhoto && (
            <IonThumbnail slot="start" className="post-photo-thumbnail">
              <img
                src={media?.webviewPath}
                alt="response attachment"
                style={{ borderRadius: '8px' }}
              />
            </IonThumbnail>
          )}
          {!loading && media?.webviewPath && isVideo && (
            <IonIcon
              style={{ marginRight: '1rem', color: 'inherit' }}
              icon={videocam}
            />
          )}
          {!loading && media?.webviewPath && isPdf && (
            <PdfIndicator attachment={{ media, name: media?.webviewPath?.name }} overrideStyle={{ margin: '.5rem 0 0 0' }} />
          )}
          <IonLabel>
            {displayAttachmentLabel(loading, media, true)}
          </IonLabel>
          <IonButton fill="clear" onClick={handleMediaClose}>
            <IonIcon style={{ color: 'grey' }} slot="icon-only" icon={close} />
          </IonButton>
        </IonItem>
      )}

      {/* Media Upload Actions */}
      <Stack direction="row" className="response-actions" justifyContent="space-between">
        <Stack className="response-actions-buttons" direction="row" spacing={1} sx={{ marginLeft: '10px' }}>
          <IonItem
            className="media-action-button"
            lines="none"
            onClick={handlePresentActionSheet}
          >
            <IonIcon src="assets/icon/addPhoto.svg" />
          </IonItem>
          <IonItem
            onClick={handlePdf}
            className="media-action-button"
            lines="none"
          >
            <IonIcon
              src="assets/icon/file-pdf.svg"
              style={{
                width: '27px',
                height: '27px',
              }}
            />
          </IonItem>
          <IonItem
            onClick={handleHandClick}
            className="media-action-button"
            lines="none"
          >
            <IonIcon src={isHandFilled ? '/assets/icon/hand_blue_filled.svg' : '/assets/icon/hand_blue_outline.svg'} />
          </IonItem>
        </Stack>
        <IonIcon
          style={{ width: '37px', height: '37px', cursor: 'pointer' }}
          onClick={handleSubmit}
          src="/assets/icon/catchup/orange-send-button.svg"
          disabled={!response.trim() && !media}
        />
      </Stack>
    </Box>
  );
};

export default ResponseBox;
